import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'
import type { Database } from '@/lib/types/database'
import { invalidateCache } from '@/lib/query-cache'
import { resolveActiveOrganizationId } from '@/lib/organization-context'
import { useScopedRead } from './useScopedRead'

type BrandSettings = Database['public']['Tables']['brand_settings']['Row']

// Editing always reads a fresh baseline. AppLayout owns its display cache separately.
export function useBrandSettings(organizationId?: string | null) {
  const user = useAuthStore(s => s.user)
  const activeOrgId = useAuthStore(s => s.profile?.active_org_id)
  const useOrgBranding = useAuthStore(s => s.profile?.use_org_branding)
  const scopeKey = JSON.stringify([user, organizationId, activeOrgId, useOrgBranding])
  const [state, setState] = useState<{ key: string; settings: BrandSettings | null; error: boolean; loading: boolean } | null>(null)
  const generation = useRef(0)
  const activeScope = useRef<string | null>(null)
  const settings = state?.key === scopeKey ? state.settings : null
  const error = state?.key === scopeKey && state.error
  const loading = !!user && (state?.key !== scopeKey || state.loading)

  const fetchSettings = useCallback(async () => {
    const version = ++generation.current
    if (!user) return
    setState(previous => ({ key: scopeKey, settings: previous?.key === scopeKey ? previous.settings : null, loading: true, error: false }))
    try {
      let data: BrandSettings | null
      if (organizationId) {
        const result = await supabase.from('brand_settings').select('*').eq('org_id', organizationId).maybeSingle()
        if (result.error) throw result.error
        data = result.data
      } else data = await fetchBrandSettingsForOwner(user.id)
      if (version === generation.current) setState({ key: scopeKey, settings: data, loading: false, error: false })
    } catch {
      if (version === generation.current) setState({ key: scopeKey, settings: null, loading: false, error: true })
    }
  }, [user, scopeKey, organizationId])

  useEffect(() => {
    activeScope.current = scopeKey
    void fetchSettings()
    return () => { generation.current += 1; activeScope.current = null }
  }, [fetchSettings, scopeKey])

  const assertWritable = useCallback(() => {
    const current = useAuthStore.getState()
    if (!user || loading || error || !state || activeScope.current !== scopeKey || current.user?.id !== user.id ||
        current.profile?.active_org_id !== activeOrgId || current.profile?.use_org_branding !== useOrgBranding) {
      throw new Error('Read the current settings before saving')
    }
  }, [user, loading, error, state, activeOrgId, useOrgBranding, scopeKey])

  const save = useCallback(
    async (updates: {
      company_name?: string
      tagline?: string | null
      logo_url?: string | null
      primary_color?: string
      secondary_color?: string | null
      tertiary_color?: string | null
      neutral_color?: string | null
      theme_name?: string | null
      font_family?: string
      email_sender_name?: string | null
      email_reply_to?: string | null
      footer_text?: string | null
      allow_user_override?: boolean
      // Task 55. `app_icon_url` is deliberately NOT derived from `logo_url`:
      // a header logo is wide and has a wordmark in it, an app icon is square
      // and rendered at 48px behind a circular mask. Deriving one from the
      // other letterboxes unreadable text, and it does so silently.
      app_icon_url?: string | null
      // The manifest's `name`. Deliberately NOT `company_name`: that one is the
      // email sender and the page header, so a tenant who wants «PROFF Event»
      // on a phone would otherwise have to be renamed in every signature too.
      app_name?: string | null
      short_name?: string | null
    }) => {
      assertWritable()
      if (!user) throw new Error('Not authenticated')
      const version = generation.current
      // Decide scope: if the loaded settings came from an org row, write
      // back to that org. Otherwise write to the user's personal row.
      const orgId = organizationId ?? settings?.org_id ?? null
      const upsertPayload = orgId
        ? { org_id: orgId, ...updates }
        : { owner_id: user.id, ...updates }
      const onConflict = orgId ? 'org_id' : 'owner_id'
      const { data, error } = await supabase
        .from('brand_settings')
        .upsert(upsertPayload, { onConflict })
        .select()
        .single()
      if (error) throw error
      if (version === generation.current) setState({ key: scopeKey, settings: data, loading: false, error: false })
      // Bust cache so other consumers re-fetch updated branding on next mount.
      invalidateCache(`brand_settings:${user.id}`)
      return data
    },
    [user, settings?.org_id, organizationId, scopeKey, assertWritable]
  )

  const uploadLogo = useCallback(
    async (file: File): Promise<string> => {
      assertWritable()
      if (!user) throw new Error('Not authenticated')
      const ext = file.name.split('.').pop()
      // Path: org-scoped uploads go under `org/<id>/`, personal under `<userId>/`
      const orgId = organizationId ?? settings?.org_id
      const uploadScope = orgId ? `org/${orgId}` : user.id
      const path = `${uploadScope}/logo.${ext}`
      const { error } = await supabase.storage
        .from('brand-assets')
        .upload(path, file, {
          upsert: true,
          contentType: file.type,
          cacheControl: '31536000',
        })
      if (error) throw error
      const {
        data: { publicUrl },
      } = supabase.storage.from('brand-assets').getPublicUrl(path)
      return publicUrl
    },
    [user, settings?.org_id, organizationId, assertWritable]
  )

  return { settings, loading, error, save, uploadLogo, refetch: fetchSettings }
}

/**
 * Fetch the EFFECTIVE brand settings for a user — org-scoped first,
 * personal as fallback.
 *
 * Resolution order:
 *   1. Read profile.use_org_branding + profile.active_org_id
 *   2. If use_org_branding=true → validate the pointer against current
 *      memberships, otherwise use the deterministic oldest membership
 *   3. Else → fetch the user's personal brand_settings (owner_id match)
 *
 * ⚠️ STEP 2's FALLBACK IS THE POINT, and it is not a nicety.
 *
 * `active_org_id` is a POINTER at one of several orgs, not the record of
 * membership — `organization_members` is. The app's own model says so
 * (`useMyOrganization`: «If not set, the oldest-joined org is the active
 * default», and line 133 implements exactly that). This function used to
 * require the pointer, so it disagreed with the hook every other org-aware
 * surface reads, and the disagreement was silent: a member with a null pointer
 * simply got the EventRipple default palette and no error anywhere.
 *
 * Measured 12.08.2026: FOUR of Nortura PROFF's five members — every admin
 * except the owner — carry `use_org_branding = true` with `active_org_id`
 * null, and all four had signed in that same day. They were being shown an
 * unbranded app while the tenant's colours, font, icon and dark default sat
 * complete in a row nothing would read for them.
 *
 * Resolving from membership fixes it for every future member too, which a
 * backfill of those four rows would not: nothing on the join path sets the
 * pointer, so the next person invited would arrive in the same state.
 *
 * This is the right hook for AppLayout (decides which CSS vars to apply
 * for the logged-in user's shell).
 */
export async function fetchBrandSettingsForOwner(
  ownerId: string
): Promise<BrandSettings | null> {
  // 1) Read profile to decide which scope to use
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('use_org_branding, active_org_id')
    .eq('id', ownerId)
    .maybeSingle()

  if (profileError) throw profileError

  // 2) Org-scoped branding when applicable
  if (profile?.use_org_branding) {
    const { data: memberships, error: membershipError } = await supabase
      .from('organization_members')
      .select('org_id, joined_at')
      .eq('user_id', ownerId)
      .order('joined_at', { ascending: true })
      .order('org_id', { ascending: true })
    if (membershipError) throw membershipError
    const orgId = resolveActiveOrganizationId(
      profile.active_org_id,
      memberships ?? [],
    )
    if (orgId) {
      const { data: orgBrand, error: brandError } = await supabase
        .from('brand_settings')
        .select('*')
        .eq('org_id', orgId)
        .maybeSingle()
      if (brandError) throw brandError
      if (orgBrand) return orgBrand
    }
  }

  // 2b) Opted OUT — but the organisation may have LOCKED branding on.
  //
  // `brand_settings.allow_user_override = false` is the admin saying «everyone
  // in the organisation always uses this brand and cannot turn it off for
  // themselves», which is what the Branding tab's switch promises in those
  // words. It had promised it since 26.03.2026 and nothing ever read the
  // column: measured 13.10.2026, the only references in the app were the
  // switch that writes it, the draft that carries it, and the row type.
  // Nortura PROFF has carried `false` the whole time, so the one tenant
  // relying on it was the one tenant it did not work for.
  //
  // Enforced HERE, at the single point the effective brand is resolved, and
  // not by disabling the member's toggle: a member who had already opted out
  // carries a stored `use_org_branding = false`, and a UI-only fix would leave
  // every one of those rows exempt forever. The lock is read, not the history.
  //
  // Scoped to the opted-out path on purpose. An opted-IN member has already
  // returned above without the extra round trip, and a member of no
  // organisation falls straight through to their personal row as before.
  const { data: lockMemberships, error: lockMembershipError } = await supabase
    .from('organization_members')
    .select('org_id, joined_at')
    .eq('user_id', ownerId)
    .order('joined_at', { ascending: true })
    .order('org_id', { ascending: true })
  if (lockMembershipError) throw lockMembershipError
  const lockedOrgId = resolveActiveOrganizationId(
    profile?.active_org_id ?? null,
    lockMemberships ?? [],
  )
  if (lockedOrgId) {
    const { data: lockedBrand, error: lockedBrandError } = await supabase
      .from('brand_settings')
      .select('*')
      .eq('org_id', lockedOrgId)
      .maybeSingle()
    if (lockedBrandError) throw lockedBrandError
    // `=== false`, never `!`: the column is NOT NULL DEFAULT TRUE, so a row
    // that is missing or unreadable must not be read as a lock.
    if (lockedBrand && lockedBrand.allow_user_override === false) return lockedBrand
  }

  // 3) Personal fallback
  const { data, error } = await supabase
    .from('brand_settings')
    .select('*')
    .eq('owner_id', ownerId)
    .maybeSingle()
  if (error) throw error
  return data
}

/** Fetch brand settings for public offer view — gated on premium subscription via RPC */
export async function fetchBrandSettingsForOffer(
  ownerId: string
): Promise<BrandSettings | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await (supabase.rpc as any)('get_brand_settings_for_offer', {
    p_owner_id: ownerId,
  }) as { data: BrandSettings[] | null }
  return data?.[0] ?? null
}

/** User-level preference: opt in/out of org branding on their own offers */
export function useOrgBrandingPreference() {
  const userId = useAuthStore((s) => s.user?.id)
  const read = useCallback(async () => {
    if (!userId) throw new Error('Not authenticated')
    const { data, error } = await supabase.from('profiles').select('use_org_branding').eq('id', userId).single()
    if (error) throw error
    if (!data || typeof data.use_org_branding !== 'boolean') throw new Error('branding_preference_unacknowledged')
    return data.use_org_branding
  }, [userId])
  const state = useScopedRead('org-branding-preference', read)
  const pending = useRef(false)
  const [saving, setSaving] = useState(false)
  const setUseOrgBranding = useCallback(async (value: boolean): Promise<void> => {
    if (!userId || pending.current || state.loading || state.error || state.data === null) throw new Error('Read the current preference before saving')
    pending.current = true
    setSaving(true)
    try {
      await state.mutate(async () => {
        const { data, error } = await supabase.from('profiles')
          .update({ use_org_branding: value }).eq('id', userId).select('use_org_branding').single()
        if (error) throw error
        if (!data || data.use_org_branding !== value) throw new Error('branding_preference_unacknowledged')
        return value
      }, (_, saved) => saved)
      if (useAuthStore.getState().user?.id === userId) {
        const profile = useAuthStore.getState().profile
        if (profile) useAuthStore.getState().setProfile({ ...profile, use_org_branding: value })
        invalidateCache(`brand_settings:${userId}`)
      }
    } finally {
      pending.current = false
      setSaving(false)
    }
  }, [userId, state.loading, state.error, state.data, state.mutate])
  return { useOrgBranding: state.data ?? false, setUseOrgBranding, saving, loading: state.loading, error: state.error, refetch: state.reload }
}
